import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { AuthService } from '../../_core/_services/auth.service';
import { NotificationService } from '../../_core/_services/notification.service';

type PinForm = FormGroup<{
  pin: FormControl<string>;
}>;

@Component({
  selector: 'app-confirm-pin',
  standalone: false,
  templateUrl: './confirm-pin.html',
  styleUrl: './confirm-pin.css',
})
export class ConfirmPin implements OnDestroy {
  loading = false;
  submitted = false;
  error = '';
  success = '';

  resendLoading = false;
  resendMessage = '';
  cooldownSeconds = 0;

  private timerSub?: Subscription;

  form!: PinForm;

  constructor(
    private notifyService: NotificationService,
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router
  ) {
    this.form = this.fb.nonNullable.group({
      pin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    if (!this.auth.tempToken) {
      this.router.navigateByUrl('/auth/login');
    }
  }

  submit() {
    this.submitted = true;
    this.error = '';
    this.success = '';

    if (this.form.invalid) return;

    this.loading = true;
    const { pin } = this.form.getRawValue();

    this.auth.confirmPin(pin).subscribe({
      next: (res) => {
        if (res?.success) {
          this.success = res?.message || 'Email verified successfully.';

        this.notifyService.showSuccess(this.success!, 'Success');
          setTimeout(() => this.router.navigateByUrl('/dashboard'), 500);
        } else {
          this.error = res?.message || res?.error?.code || 'Verification code confirmation failed.';

        this.notifyService.showError(this.error!, 'Error');
        }
      },
      error: (err) => {
        const apiBody = err?.error;
        this.error =
          apiBody?.message ??
          apiBody?.Message ??
          apiBody?.error?.code ??
          apiBody?.Error?.Code ??
          err?.message ??
          'Verification code confirmation failed.';

        this.notifyService.showError(this.error!, 'Error');
        this.loading = false;
      },
      complete: () => (this.loading = false),
    });
  }

  canResend(): boolean {
    return !this.resendLoading && this.cooldownSeconds === 0;
  }

  resendPin() {
    if (!this.canResend()) return;

    this.resendLoading = true;
    this.resendMessage = '';
    this.error = '';

    this.auth.resendPin().subscribe({
      next: (res) => {
        if (res?.success) {
          this.resendMessage = res?.message || 'A new verification code has been sent to your email.';
          this.notifyService.showSuccess(this.resendMessage!, 'Success');
          this.startCooldown(90);
        } else {
          this.error = res?.message || res?.error?.code || 'Failed to resend verification code.';
          this.notifyService.showError(this.error!, 'Error');
        }
      },
      error: (err) => {
        const apiBody = err?.error;
        this.error =
          apiBody?.message ??
          apiBody?.Message ??
          apiBody?.error?.code ??
          apiBody?.Error?.Code ??
          err?.message ??
          'Failed to resend verification code.';

        this.notifyService.showError(this.error!, 'Error');
      },
      complete: () => (this.resendLoading = false),
    });
  }

  private startCooldown(seconds: number) {
    this.timerSub?.unsubscribe();
    this.cooldownSeconds = seconds;

    this.timerSub = interval(1000).subscribe(() => {
      this.cooldownSeconds = Math.max(0, this.cooldownSeconds - 1);
      if (this.cooldownSeconds === 0) this.timerSub?.unsubscribe();
    });
  }

  ngOnDestroy() {
    this.timerSub?.unsubscribe();
  }
}
